/**
 * Transfer_Service API client (task 21.2).
 *
 * | Method / Path                | Function                  | Req.       |
 * | ---------------------------- | ------------------------- | ---------- |
 * | `POST /transfers/beneficiary`| {@link transferToBeneficiary} | R3.5   |
 * | `POST /transfers/internal`   | {@link transferInternal}  | R4.4       |
 *
 * Both requests carry an `Idempotency-Key` so a retried transfer is applied at
 * most once (design: Transfer_Service). Validation and business-rule failures
 * (`INVALID_AMOUNT`, `INVALID_REFERENCE`, `SAME_ACCOUNT`, `INSUFFICIENT_FUNDS`,
 * `NOT_AUTHORIZED`) surface as an {@link ApiError} the transfer views render
 * inline.
 *
 * Both bodies accept an optional statement `reference` (1–30 characters from
 * `A-Za-z0-9 .,-/&'`). The views omit the key entirely when the customer left
 * it blank, so the service stores `null` rather than an empty string.
 */

import { apiPost, newIdempotencyKey } from './client.js';
import type { TransferResult } from './types.js';

/** Request body for a transfer to a saved beneficiary (R3.5). */
export interface BeneficiaryTransferInput {
  readonly sourceAccountId: string;
  readonly beneficiaryId: string;
  /** Decimal-string amount, 0.01–999,999,999.99, ≤2 decimals (R3.7). */
  readonly amount: string;
  /** Optional payment reference, 1–30 chars; omit when blank. */
  readonly reference?: string;
}

/** Request body for an inter-account transfer (R4.4). */
export interface InternalTransferInput {
  readonly sourceAccountId: string;
  readonly destinationAccountId: string;
  /** Decimal-string amount, 0.01–999,999,999.99, ≤2 decimals (R4.6). */
  readonly amount: string;
  /** Optional payment reference, 1–30 chars; omit when blank. */
  readonly reference?: string;
}

/** Transfer funds to a beneficiary (R3.5), debiting the source account. */
export function transferToBeneficiary(
  input: BeneficiaryTransferInput,
): Promise<TransferResult> {
  return apiPost<TransferResult>('/transfers/beneficiary', input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Transfer funds between two of the customer's own accounts (R4.4). */
export function transferInternal(
  input: InternalTransferInput,
): Promise<TransferResult> {
  return apiPost<TransferResult>('/transfers/internal', input, {
    idempotencyKey: newIdempotencyKey(),
  });
}
